/**
 * “Remove Document” confirmation — portal shell aligned with reimbursement section
 * submit / resident list dialogs; destructive red styling.
 */

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, Trash2, X } from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import type { RemoveDocumentConfirmDialogProps } from "./RemoveDocumentConfirmDialog.types";

export type { RemoveDocumentConfirmDialogProps } from "./RemoveDocumentConfirmDialog.types";

const DEFAULT_EVIDENCE_HIGHLIGHT = "default evidence";

function RemoveDocumentMessage({ message }: { message: string }) {
  const highlight = DEFAULT_EVIDENCE_HIGHLIGHT;
  const index = message.indexOf(highlight);
  if (index === -1) {
    return <>{message}</>;
  }
  return (
    <>
      {message.slice(0, index)}
      <strong className="font-bold">{highlight}</strong>
      {message.slice(index + highlight.length)}
    </>
  );
}

export function RemoveDocumentConfirmDialog({
  isOpen,
  title = "Remove this document?",
  message = "Please confirm that you really want to remove this default evidence from this case",
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  overlayDataTestId,
  primaryButtonDataTestId,
  secondaryButtonDataTestId,
  closeButtonDataTestId,
}: RemoveDocumentConfirmDialogProps) {
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex animate-fadeIn items-center justify-center bg-black/50 p-4"
      data-testid={overlayDataTestId}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-lg animate-slideUp overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-document-confirm-title"
        aria-describedby="remove-document-confirm-description"
      >
        <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-red-200 bg-red-50 text-red-600">
              <Trash2 size={22} aria-hidden="true" />
            </div>
            <h2
              id="remove-document-confirm-title"
              className="text-xl font-bold text-gray-900"
            >
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 transition-colors hover:text-gray-600"
            aria-label="Close"
            data-testid={closeButtonDataTestId}
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <div className="p-6" id="remove-document-confirm-description">
          <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-slate-900">
            <AlertCircle
              className="mt-0.5 shrink-0 text-red-600"
              size={24}
              aria-hidden="true"
            />
            <p className="text-sm leading-relaxed text-slate-800">
              <RemoveDocumentMessage message={message} />
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:rounded-b-xl">
          <button
            type="button"
            onClick={onCancel}
            data-testid={secondaryButtonDataTestId}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:w-auto"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid={primaryButtonDataTestId}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 sm:w-auto"
          >
            <Trash2 size={16} aria-hidden="true" />
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
